import React from "react";
import { cls } from "cls";
import { FeatureModel, LeadGenStyle } from "../Models/PageCreatorPage";
import LeadGenFeatureRow from "./LeadGenFeatureRow";
import LeadGenFeatureGrid from "./LeadGenFeatureGrid";
import LeadGenFeatureContainer from "./LeadGenFeatureContainer";
import { getSanitizedHtml } from "../Utilities/htmlInputUtility";

interface LeadGenFeatureProps extends FeatureModel {
  sectionStyle: LeadGenStyle;
}

const LeadGenFeature = ({
  linkStyle,
  backgroundColor,
  hasDropshadow,
  hasOutline,
  containerColor,
  outlineColor,
  headerTextColor,
  bodyTextColor,
  header,
  bodyText,
  imageStyle,
  items,
  hasBottomPadding,
  hasTopPadding,
  sectionStyle,
  featuresPerRow,
}: LeadGenFeatureProps): JSX.Element => {
  const displayFeatureStyle = () => {
    switch (sectionStyle) {
      case LeadGenStyle.FeatureRow:
        return (
          <LeadGenFeatureRow
            imageStyle={imageStyle}
            items={items}
            linkStyle={linkStyle}
            hasDropshadow={hasDropshadow}
            hasOutline={hasOutline}
            containerColor={containerColor}
            outlineColor={outlineColor}
            hasRoundedBorder
            featuresPerRow={featuresPerRow}
          />
        );
      case LeadGenStyle.FeatureGrid:
        return (
          <LeadGenFeatureGrid
            imageStyle={imageStyle}
            items={items}
            linkStyle={linkStyle}
            hasDropshadow={hasDropshadow}
            hasOutline={hasOutline}
            containerColor={containerColor}
            outlineColor={outlineColor}
            hasRoundedBorder
          />
        );
      case LeadGenStyle.FeatureContainer:
        return (
          <LeadGenFeatureContainer
            imageStyle={imageStyle}
            items={items}
            linkStyle={linkStyle}
            hasDropshadow={hasDropshadow}
            hasOutline={hasOutline}
            containerColor={containerColor}
            outlineColor={outlineColor}
            hasRoundedBorder={hasTopPadding && hasBottomPadding}
            sectionStyle={sectionStyle}
          />
        );

      default:
        return <></>;
    }
  };

  return (
    <div
      id="feature-wrapper"
      className={cls(
        "px-6",
        "md:px-12",
        hasBottomPadding ? ["pb-12", "md:pb-24"] : "pb-0",
        hasTopPadding ? ["pt-12", "md:pt-24"] : "pt-0",
        "relative",
      )}
      style={{ backgroundColor }}
    >
      <div
        id="feature-content"
        className={cls(
          "flex",
          "flex-col",
          "space-y-6",
          sectionStyle === LeadGenStyle.FeatureRow ? "lt:space-y-10" : "",
          sectionStyle === LeadGenStyle.FeatureGrid
            ? "lg:space-y-0 lg:space-x-12 lg:flex-row"
            : "lg:flex-col",
          "max-w-6xl",
          "mx-auto",
        )}
      >
        {(header || bodyText) && (
          <div
            id="feature-text"
            className="flex flex-col space-y-1 lt:space-y-2"
          >
            {header && (
              <h2
                className={`${cls(
                  "flex-wrap",
                  "mb-0",
                  "text-3xl",
                  "text-center",
                  "font-bold",
                  sectionStyle === LeadGenStyle.FeatureGrid
                    ? "lg:text-left"
                    : "lg:text-center",
                  sectionStyle === LeadGenStyle.FeatureGrid
                    ? "mx-auto lg:mx-0"
                    : "mx-auto",
                  "max-w-2xl",
                  "md:text-4xl",
                  "lg:text-5xl",
                )} [text-wrap:balance] line-height: unset`}
                style={{ color: headerTextColor, textShadow: "none" }}
              >
                {header}
              </h2>
            )}
            {bodyText && (
              <p
                className={`${cls(
                  "m-0",
                  "text-base",
                  "flex-wrap",
                  "text-center",
                  "leading-tight",
                  sectionStyle === LeadGenStyle.FeatureGrid
                    ? "lg:text-left"
                    : "lg:text-center",
                  "max-w-2xl",
                  "mx-auto",
                )} [text-wrap:balance]`}
                style={{ color: bodyTextColor }}
              >
                {getSanitizedHtml(bodyText)}
              </p>
            )}
          </div>
        )}
        {displayFeatureStyle()}
      </div>
    </div>
  );
};

export default LeadGenFeature;
